/**
 * FormsDslPanel — admin workbench for the Forms/Import DSL (Phase 2c host).
 * Left: a connection/database picker + the DSL script. Right: live preview — the
 * script is analyzed into a Plan and rendered/driven by FormsDslRuntime, running
 * its queries/actions against the chosen connection. This is the editor's
 * live-preview surface; persistence + a polished editor come later.
 */
import { createSignal, createResource, createMemo, onMount, Show, For } from 'solid-js';
import { api } from '../lib/api.js';
import { analyzeForm, listForms, getForm, createForm, updateForm, deleteForm } from '../lib/formsdsl-api.js';
import { SNIPPETS } from '../lib/formsdsl-snippets.js';
import FormsDslRuntime from '../components/FormsDslRuntime.jsx';
import FormsDslDocs from '../components/FormsDslDocs.jsx';
import CodeMirrorDSL from '../components/CodeMirrorDSL.jsx';
import { toast } from '../lib/toast.js';
import { fdslEditorSplit, setFdslEditorSplit } from '../lib/prefs.js';
import { makeSplitterDrag } from '../lib/splitter.js';

const EXAMPLE = `meta { title = "Schema explorer (demo)" }

select tbl {
  label = "Tabel"
  query = "SELECT TABLE_NAME FROM information_schema.TABLES WHERE TABLE_SCHEMA = DATABASE() ORDER BY TABLE_NAME"
  value = TABLE_NAME
}

text q { label = "Cari kolom" }

table cols {
  label = "Kolom"
  query = "SELECT COLUMN_NAME, DATA_TYPE, IS_NULLABLE FROM information_schema.COLUMNS WHERE TABLE_SCHEMA = DATABASE() AND TABLE_NAME = :tbl AND search(:q, COLUMN_NAME) ORDER BY ORDINAL_POSITION"
  value = COLUMN_NAME
  show  = [COLUMN_NAME, DATA_TYPE, IS_NULLABLE]
}`;

export default function FormsDslPanel(props) {
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [connId, setConnId] = createSignal('');
  const [database, setDatabase] = createSignal('');
  const [script, setScript] = createSignal(EXAMPLE);
  const [plan, setPlan] = createSignal(null);
  const [busy, setBusy] = createSignal(false);
  const [snipOpen, setSnipOpen] = createSignal(false);
  const [docsOpen, setDocsOpen] = createSignal(false);
  const [formName, setFormName] = createSignal('');
  const [formId, setFormId] = createSignal(null); // loaded saved-form id, or null (new)
  const [saving, setSaving] = createSignal(false);
  const [saved, { refetch: refetchSaved }] = createResource(async () => {
    try { return (await listForms()).forms || []; } catch { return []; }
  });
  let cmView;

  // ── Editor/preview splitter ─────────────────────────────────────────────────
  // Drag the bar between the DSL editor and the live preview to resize. Ratio is
  // the fraction of horizontal space given to the editor; clamped + persisted.
  let splitHostEl;
  const startSplitterDrag = makeSplitterDrag(() => splitHostEl, setFdslEditorSplit);

  // Inline diagnostics for the editor: run the script through the analyzer.
  const lint = async (code) => {
    try { return (await analyzeForm(code)).plan?.diagnostics || []; } catch { return []; }
  };

  // Snippets grouped for the palette.
  const snippetGroups = createMemo(() => {
    const m = new Map();
    for (const s of SNIPPETS) { if (!m.has(s.group)) m.set(s.group, []); m.get(s.group).push(s); }
    return [...m.entries()];
  });

  // Insert a snippet at the cursor (via the editor view), separated by a blank line.
  const insertSnippet = (code) => {
    setSnipOpen(false);
    const v = cmView;
    if (!v) { setScript(script() ? script() + '\n\n' + code + '\n' : code + '\n'); return; }
    const pos = v.state.selection.main.head;
    const doc = v.state.doc.toString();
    const sep = pos > 0 && doc[pos - 1] !== '\n' ? '\n\n' : (pos > 0 ? '\n' : '');
    const text = sep + code + '\n';
    v.dispatch({ changes: { from: pos, insert: text }, selection: { anchor: pos + text.length } });
    v.focus();
  };

  const analyze = async () => {
    setBusy(true);
    try {
      const r = await analyzeForm(script());
      setPlan(r.plan);
      const errs = (r.plan?.diagnostics || []).length;
      if (errs) toast.error(`${errs} diagnostic${errs === 1 ? '' : 's'} — see preview`);
      else toast.success('Analyzed');
    } catch (e) {
      toast.error('Analyze failed: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const canPreview = () => plan() && connId();

  // --- persistence (save / open / new / delete) ---
  const save = async () => {
    const name = formName().trim();
    if (!name) { toast.error('Give the form a name first.'); return; }
    setSaving(true);
    try {
      const body = { name, script: script(), connectionId: Number(connId()) || 0, database: database() };
      const r = formId() ? await updateForm(formId(), body) : await createForm(body);
      setFormId(r.form.id);
      toast.success('Saved');
      refetchSaved();
    } catch (e) {
      toast.error('Save failed: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };
  const openForm = async (id) => {
    if (!id) return;
    try {
      const f = (await getForm(id)).form;
      setFormId(f.id); setFormName(f.name); setScript(f.script || '');
      setConnId(f.connectionId ? String(f.connectionId) : '');
      setDatabase(f.database || '');
      setPlan(null);
      toast.success(`Opened “${f.name}”`);
    } catch (e) { toast.error('Open failed: ' + (e?.message || e)); }
  };
  const newForm = () => { setFormId(null); setFormName(''); setScript(''); setPlan(null); };

  // When hosted by the Forms tab (Edit), open the requested form on mount.
  onMount(() => { if (props.initialFormId) openForm(props.initialFormId); });
  const removeForm = async () => {
    if (!formId()) return;
    if (!confirm(`Delete form “${formName()}”?`)) return;
    try { await deleteForm(formId()); toast.success('Deleted'); newForm(); refetchSaved(); }
    catch (e) { toast.error('Delete failed: ' + (e?.message || e)); }
  };

  return (
    <div class="fdsl-panel">
      <div class="fdsl-toolbar">
        <Show when={props.onBack}>
          <button class="btn btn-sm btn-ghost" onClick={() => props.onBack()}>← Back</button>
        </Show>
        <select class="input fdsl-sel" value={connId()} onChange={(e) => setConnId(e.currentTarget.value)}>
          <option value="">— connection —</option>
          <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select>
        <input class="input fdsl-db" placeholder="database" value={database()} onInput={(e) => setDatabase(e.currentTarget.value)} />
        <button class="btn btn-primary btn-sm" disabled={busy()} onClick={analyze}>{busy() ? 'Analyzing…' : '▶ Analyze'}</button>
        <div class="fdsl-snip">
          <button class="btn btn-sm btn-ghost" onClick={() => setSnipOpen((v) => !v)}>＋ Snippet ▾</button>
          <Show when={snipOpen()}>
            <div class="fdsl-snip-menu">
              <For each={snippetGroups()}>{([group, items]) => (
                <div class="fdsl-snip-group">
                  <div class="fdsl-snip-gtitle">{group}</div>
                  <For each={items}>{(s) => (
                    <button class="fdsl-snip-item" onClick={() => insertSnippet(s.code)} title={s.desc}>
                      <span class="fdsl-snip-name">{s.name}</span>
                      <span class="fdsl-snip-desc">{s.desc}</span>
                    </button>
                  )}</For>
                </div>
              )}</For>
            </div>
          </Show>
        </div>
        <button class="btn btn-sm btn-ghost" onClick={() => setDocsOpen(true)} title="Forms DSL documentation, reference & examples">📖 Docs</button>
        <span class="spacer" />
        <span class="muted" style="font-size:11.5px;">DSL workbench (admin)</span>
      </div>

      <FormsDslDocs open={docsOpen()} onClose={() => setDocsOpen(false)} onInsert={insertSnippet} />

      <div class="fdsl-toolbar fdsl-toolbar-2">
        <input class="input fdsl-name" placeholder="Form name" value={formName()} onInput={(e) => setFormName(e.currentTarget.value)} />
        <button class="btn btn-sm btn-primary" disabled={saving()} onClick={save}>{saving() ? 'Saving…' : (formId() ? '💾 Save' : '💾 Save new')}</button>
        <select class="input fdsl-open" value={formId() || ''} onChange={(e) => openForm(e.currentTarget.value)}>
          <option value="">📂 Open saved…</option>
          <For each={saved() || []}>{(f) => <option value={f.id}>{f.name}</option>}</For>
        </select>
        <button class="btn btn-sm btn-ghost" onClick={newForm}>＋ New</button>
        <Show when={formId()}><button class="btn btn-sm btn-ghost" onClick={removeForm} title="Delete this form">🗑</button></Show>
      </div>

      <div class="fdsl-split" ref={splitHostEl}>
        <div class="fdsl-editor" style={`flex-basis: ${(fdslEditorSplit() * 100).toFixed(2)}%;`}>
          <CodeMirrorDSL value={script()} onInput={setScript} lint={lint} onViewReady={(v) => (cmView = v)} />
        </div>
        <div class="fdsl-splitter" onPointerDown={startSplitterDrag} title="Drag to resize" />
        <div class="fdsl-preview">
          <Show when={canPreview()} fallback={
            <div class="fdsl-empty">{plan() ? 'Pick a connection to run the preview.' : 'Write a script and click Analyze.'}</div>
          }>
            <FormsDslRuntime plan={plan()} connectionId={connId()} database={database()} />
          </Show>
        </div>
      </div>
    </div>
  );
}
